import { AccessoryColor } from '@/enums';
import { RoofArticle, RoofForm, RoofModel, TileFamily, MarketStr } from './common';
import { RoofTile } from './rooftiles/rooftile';
import { addResult, addResults, Category, removeZeroQtyRows, type ResultRow } from './result';
import type { Roof } from './roof';

/** Class representing the SAFE configuration of a roof. Used to calculate all SAFE articles to add to summary */
export class SAFECalc {
  public roof: Roof;

  public selColor = AccessoryColor.None;
  public selBattenSteps = 0;
  public selMountingRail = 0;
  public calcTrackTilesForBattenSteps = false;
  public selSlipProtection = 0;
  public selRoofHatch = 0;
  public selSafetyHook = 0;
  public selSnowHook = 0;
  public selIceStopper = 0;

  public battenStepArtNo = '';
  public mountingRailArtNo = '';
  public slipProtectionArtNo = '';
  public roofHatchArtNo = '';
  public safetyHookArtNo = '';
  public snowHookArtNo = '';
  public fasteningEyeArtNo = '171690';
  public snowSlideObstacleArtNo = '';
  public snowSlideObstacleKonsolArtNo = '';
  public iceStopperArtNo = '';

  public useSAFE = false;
  public snowSafetyLengths: number[] = [];
  public snowSafetyRaisedLengths: number[] = [];
  public roofBridgeLengths: number[] = [];
  public hatchSafetyLengths: number[] = [];
  public roofRailsAmount = 0;
  public snowZone = 0;
  public fasteningEyeAmount = 0;
  public selSnowSlideProtection = false;

  protected snowProtectionLength = 2.1;
  protected roofRailLength = 2.1;

  protected calcResults = {
    snowSlideConsoles: 0,
    snowSafety: 0,
    snowSafetyConsoles: 0,
    elevationMounts: 0,
    roofBridgeLong: 0,
    roofBridgeShort: 0,
    roofBridgeConsoles: 0,
    snowSafetyRaisedConsoles: 0,
    roofRail: 0,
    roofRailConsoles: 0,
    footPlats: 0,
    roofRailSkarvar: 0,
  };

  public constructor(roof: Roof) {
    this.roof = roof;
    this.roof.safe = this;
  }

  public getSnowSafetyArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '172420';
      case AccessoryColor.BrickRed:
        return '172424';
      case AccessoryColor.GalvanizedGreySilver:
        return '172490';
    }
    return '';
  }

  public getSnowSafetyConsoleArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '178320';
      case AccessoryColor.BrickRed:
        return '178324';
      case AccessoryColor.GalvanizedGreySilver:
        return '178390';
    }
    return '';
  }

  public getElevationMountArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '171820';
      case AccessoryColor.BrickRed:
        return '171824';
      case AccessoryColor.GalvanizedGreySilver:
        return '171890';
    }
    return '';
  }

  public getRoofBridgeArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '172220';
      case AccessoryColor.BrickRed:
        return '172224';
      case AccessoryColor.GalvanizedGreySilver:
        return '172290';
    }
    return '';
  }

  public getRoofBridgeShortArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '170220';
      case AccessoryColor.BrickRed:
        return '170224';
      case AccessoryColor.GalvanizedGreySilver:
        return '170290';
    }
    return '';
  }

  public getRoofBridgeConsoleArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '170120';
      case AccessoryColor.BrickRed:
        return '170124';
      case AccessoryColor.GalvanizedGreySilver:
        return '170190';
    }
    return '';
  }

  public getRoofRailArtNo(): string {
    const roofRailBase = '1726';
    switch (this.selColor) {
      case AccessoryColor.Black:
        return `${roofRailBase}20`;
      case AccessoryColor.BrickRed:
        return `${roofRailBase}24`;
      case AccessoryColor.GalvanizedGreySilver:
        return `${roofRailBase}90`;
    }
    return '';
  }

  public getRoofRailConsoleArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '170520';
      case AccessoryColor.BrickRed:
        return '170524';
      case AccessoryColor.GalvanizedGreySilver:
        return '170590';
    }
    return '';
  }

  public getRoofHatchRailArtNo(): string {
    let base = '';
    switch (this.roof.selection.tileFamily.family) {
      case TileFamily.Palema:
      case TileFamily.Exklusiv:
      case TileFamily.HansaFalsatLertegel:
      case TileFamily.HansaFalsatLertegelRakFramkant:
      case TileFamily.TvillingLertegelRakFramkant:
      case TileFamily.TvillingFalsatLertegel:
        base = '1728';
        break;
      case TileFamily.Carisma:
      case TileFamily.PianoFalsatLertegel:
        base = '1729';
        break;
    }
    switch (this.selColor) {
      case AccessoryColor.Black:
        return base + '20';
      case AccessoryColor.BrickRed:
        return base + '24';
      case AccessoryColor.GalvanizedGreySilver:
        return base + '90';
    }
    return '';
  }

  public getRoofHatchArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '170820';
      case AccessoryColor.BrickRed:
        return '170824';
      case AccessoryColor.GalvanizedGreySilver:
        return '170890';
    }
    return '';
  }

  public getRoofHatchConsoleArtNo(): string {
    switch (this.selColor) {
      case AccessoryColor.Black:
        return '170720';
      case AccessoryColor.BrickRed:
        return '170724';
      case AccessoryColor.GalvanizedGreySilver:
        return '170790';
    }
    return '';
  }

  public getFootPlatArtNo(): string {
    switch (this.roof.selection.tileFamily.family) {
      case TileFamily.Palema:
      case TileFamily.Exklusiv:
      case TileFamily.HansaFalsatLertegel:
      case TileFamily.HansaFalsatLertegelRakFramkant:
      case TileFamily.TvillingLertegelRakFramkant:
      case TileFamily.TvillingFalsatLertegel:
        return '170990';
      case TileFamily.Carisma:
      case TileFamily.PianoFalsatLertegel:
        return '171090';
    }
    return '';
  }

  public getTakfall() {
    if (!this.roof.mainPart) return 0;

    const takfall = this.roof.mainPart.getTakfall();
    let t = takfall.takfall / 1000;
    if (this.roof.mainPart.model === RoofModel.Mansard) t += takfall.takfall2 / 1000;
    return t;
  }

  public getTakvinkel(): number {
    if (!this.roof.mainPart) return 0;

    if (this.roof.mainPart.model === RoofModel.Standard) return this.roof.mainPart.angle;

    return Math.max(this.roof.mainPart.angle, this.roof.mainPart.angle2);
  }

  public getTakTyp(): RoofForm {
    if (this.roof?.mainPart?.form === RoofForm.Pult) return RoofForm.Pult;

    return RoofForm.Sadel;
  }

  public getTileData(): { maxDistanceConsole: number; multiplier: number } {
    const cc = this.roof.selection.tileFamily.takpannaByggBredd();
    const maxD = Math.ceil(600 / cc) * cc;
    const mul = Math.ceil(1000 / cc);
    return { maxDistanceConsole: maxD, multiplier: mul };
  }

  protected amountOfAccessories(lengthsArr: number[], productLength = 1, indent = 0, interval = 0) {
    let total = 0;
    for (const len of lengthsArr) {
      let units = Math.floor(len / productLength);
      if (len - units * productLength > 0.5) units++;
      const length = Number((units * productLength).toFixed(2)) - 0.3;

      const divideBy = interval > 0 ? interval : productLength;
      total += Math.ceil((length - indent) / divideBy) + 1;
    }
    return total;
  }

  protected roofMultiplier(): number {
    const tileData = this.getTileData();
    return (tileData.multiplier * this.roof.selection.tileFamily.takpannaByggBredd()) / 1000;
  }

  public calculateMaxTakfall(): number {
    const vinkel = this.getTakvinkel();
    let my = 0;

    switch (this.getTakTyp()) {
      case RoofForm.Sadel:
        if (vinkel <= 22.5) my = 0.8 + (0.3 / 22.5) * vinkel;
        else if (vinkel <= 53.2) my = 1.1 - (0.9 / 30.7) * (vinkel - 22.5);
        else my = 0.2;

        break;
      case RoofForm.Pult:
        if (vinkel <= 30) my = 0.8;
        else if (vinkel <= 52.5) my = (0.8 * (60 - vinkel)) / 30;
        else if (vinkel <= 60) my = 0.2;
        else my = 0.0;

        break;
    }
    return (
      (1200 * 5) /
      (this.getTileData().maxDistanceConsole *
        this.snowZone *
        my *
        Math.cos((vinkel * Math.PI) / 180) *
        Math.sin((vinkel * Math.PI) / 180))
    );
  }

  public calculateMaxDistance(): number {
    const takfallPerRad = this.getTakfall() / this.calculateRowsSnowProtection();
    const vinkel = this.getTakvinkel();
    let my = 0;

    switch (this.getTakTyp()) {
      case RoofForm.Sadel:
        if (vinkel <= 22.5) my = 0.8 + (0.3 / 22.5) * vinkel;
        else if (vinkel <= 53.2) my = 1.1 - (0.9 / 30.7) * (vinkel - 22.5);
        else my = 0.2;

        break;
      case RoofForm.Pult:
        if (vinkel <= 30) my = 0.8;
        else if (vinkel <= 52.5) my = (0.8 * (60 - vinkel)) / 30;
        else if (vinkel <= 60) my = 0.2;
        else my = 0.0;

        break;
    }

    let maxDistance = (1200 * 5) / (this.snowZone * my * Math.cos((vinkel * Math.PI) / 180) * Math.sin((vinkel * Math.PI) / 180) * takfallPerRad);
    if (maxDistance > 1200) maxDistance = 1200;
    return maxDistance;
  }

  public calculateRowsSnowProtection() {
    return Math.ceil(this.getTakfall() / this.calculateMaxTakfall());
  }

  public calculateSnowSafety(): number {
    // Varje enhet är 2.1m lång, beräkna hur många enheter det tar för att täcka alla längder.
    // BK-245: Vi kräver att resten från alla jämt beräknade snörasskydd överstiger 50cm innan det beräknas en extra
    let amountPerRow = this.snowSafetyLengths.reduce((acc, len) => (acc += Math.ceil(Math.max(0.01, len - 0.5) / this.snowProtectionLength)), 0);

    amountPerRow += 2 * this.snowSafetyRaisedLengths.reduce((acc, len) => (acc += Math.ceil(Math.max(0.01, len - 0.5) / this.snowProtectionLength)), 0);

    const rows = this.calculateRowsSnowProtection();

    this.calcResults.snowSafety = amountPerRow * rows;
    return amountPerRow * rows;
  }

  public distanceBetweenConsoles() {
    const maxDistance = Math.round(this.calculateMaxDistance() * 100) / 100
    const ccRooftile = this.roof.selection.tileFamily.takpannaByggBredd();
    return Math.floor(maxDistance / ccRooftile) * ccRooftile
  }

  public calculateDistanceConsole(): number {
    const tileData = this.getTileData();
    const cc = this.roof.selection.tileFamily.takpannaByggBredd();
    const maxDistance = this.calculateMaxDistance();

    const distanceConsole =
      maxDistance < tileData.maxDistanceConsole ? tileData.maxDistanceConsole / 1000 : (Math.floor(maxDistance / cc) * cc) / 1000;

    return distanceConsole;
  }

  public calculateSnowSafetyConsoles(): ResultRow[] {
    const distanceConsole = this.calculateDistanceConsole();
    const rows = this.calculateRowsSnowProtection();

    let amountPerRow = 0;
    for (const len of this.snowSafetyLengths) {
      const wholeLengths = Math.ceil((+len.toFixed(2) - 0.5) / this.snowProtectionLength) * this.snowProtectionLength;
      amountPerRow += Math.ceil(+wholeLengths.toFixed(2) / distanceConsole - 0.3) + 1;
    }

    for (const len of this.snowSafetyRaisedLengths) {
      const wholeLengths = Math.ceil((+len.toFixed(2) - 0.5) / this.snowProtectionLength) * this.snowProtectionLength;
      amountPerRow += Math.ceil(1.5 * +wholeLengths.toFixed(2) / distanceConsole - 0.3) + 1;
    }

    let amount = amountPerRow * rows;
    this.calcResults.snowSafetyConsoles = amount;

    const artno = this.getSnowSafetyConsoleArtNo();
    // BENT-288: 170390-10 utgår och ska inte beräknas längre, nya 10-paket artiklar ska tillkomma senare
    if (artno === '170390') {
      return [
        { art: RoofArticle.None, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} },
      ];
    }

    const nPerBundle = 10;
    const bundles = Math.floor(amount / nPerBundle);
    amount = amount % nPerBundle;

    const bundleArtno = this.getSnowSafetyConsoleArtNo() + '-10';
    return [
      { art: RoofArticle.None, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} },
      { art: RoofArticle.None, artnr: bundleArtno, total: bundles, category: Category.Taksäkerhet, origins: {} },
    ] as ResultRow[];
  }

  public calculateElevationMounts(): ResultRow[] {
    const elevMountLength = 1.2;
    let amount = this.snowSafetyRaisedLengths.reduce((acc, len) => (acc += Math.ceil(Math.max(0.01, len - 0.5) / elevMountLength + 1)), 0);
    this.calcResults.elevationMounts = amount;

    const nPerBundle = 10;
    const bundles = Math.floor(amount / nPerBundle);
    amount = amount % nPerBundle;

    const artno = this.getElevationMountArtNo();
    const bundleArtno = artno + '-10';
    return [
      { art: RoofArticle.None, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} },
      { art: RoofArticle.None, artnr: bundleArtno, total: bundles, category: Category.Taksäkerhet, origins: {} },
    ] as ResultRow[];
  }

  public async calculateSafeTrackTiles(): Promise<ResultRow[]> {
    const consoles = this.calcResults.snowSafetyConsoles + this.calcResults.roofBridgeConsoles + this.calcResults.roofRailConsoles;

    let amount = 0;
    switch (this.roof.selection.tileFamily.family) {
      case TileFamily.Carisma:
      case TileFamily.HansaFalsatLertegel:
      case TileFamily.HansaFalsatLertegelRakFramkant:
      case TileFamily.TvillingFalsatLertegel:
      case TileFamily.TvillingLertegelRakFramkant:
      case TileFamily.PianoFalsatLertegel:
        amount = consoles;
        break;
      default:
        // Exklusiv 1-kupig
        // Palema 2-kupig
        amount = consoles * 2;
    }

    amount += this.roofRailsAmount * 8;

    const results: ResultRow[] = [];
    for (const art of [
      RoofArticle.SpårPanna,
      RoofArticle.SpårPannaBörjan,
      RoofArticle.SpårPannaSlut,
      RoofArticle.SpårPannaHöger,
      RoofArticle.SpårPannaVänster,
    ]) {
      try {
        const artno = await this.roof.selection.getArticleNumber(art, false, true);
        if (artno) results.push({ art: art, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} });
      } catch (err) {
        continue;
      }
    }
    return results;
  }

  /** Calculate tracking tiles for some non-safe tiles, only used for norway */
  public async calculateTrackTiles(): Promise<ResultRow[]> {
    let trackTiles = 0, trackTilesLeft = 0, trackTilesRight = 0;

    switch (this.roof.selection.tileFamily.family) {
      case TileFamily.Palema:
      case TileFamily.Exklusiv:
        trackTiles = this.calcResults.snowSlideConsoles + (this.calcTrackTilesForBattenSteps ? 2 * this.selBattenSteps : 0);
        break;
      case TileFamily.Carisma:
        trackTilesRight = (this.calcTrackTilesForBattenSteps ? this.selBattenSteps : 0);
        trackTilesLeft = this.calcResults.snowSlideConsoles + (this.calcTrackTilesForBattenSteps ? this.selBattenSteps : 0);
        break;
    }

    const results: ResultRow[] = [];
    for (const [art, amount] of [
      [RoofArticle.SpårPanna, trackTiles],
      [RoofArticle.SpårPannaHöger, trackTilesRight],
      [RoofArticle.SpårPannaVänster, trackTilesLeft],
    ]) {
      try {
        const artno = await this.roof.selection.getArticleNumber(art, false, true);
        if (artno) results.push({ art: art, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} });
      } catch (err) {
        continue;
      }
    }
    return results;
  }

  public calculateRoofBridge(): ResultRow[] {
    const lengthLong = 2.1;
    const lengthShort = lengthLong / 2; // 1.05;
    let amountLong = 0;
    let amountShort = 0;

    this.roofBridgeLengths.forEach((len) => {
      // BK-245: Vi kräver att resten från alla jämt beräknade korta takbryggor överstiger 30cm innan det beräknas en extra
      const shortPieces = Math.ceil(Math.max(0.01, len - 0.3) / lengthShort);
      amountLong += Math.floor(shortPieces / 2);
      amountShort += Math.ceil(shortPieces % 2);
    });
    this.calcResults.roofBridgeLong = amountLong;
    this.calcResults.roofBridgeShort = amountShort;

    return [
      { art: RoofArticle.None, artnr: this.getRoofBridgeArtNo(), total: amountLong, category: Category.Taksäkerhet, origins: {} },
      { art: RoofArticle.None, artnr: this.getRoofBridgeShortArtNo(), total: amountShort, category: Category.Taksäkerhet, origins: {} },
    ] as ResultRow[];
  }

  public calculateRoofBridgeConsoles(): ResultRow[] {
    const lengthLong = 2.1;
    const lengthShort = lengthLong / 2; // 1.05;
    let amount = 0;

    this.roofBridgeLengths.forEach((len) => {
      const shortPieces = Math.ceil(Math.max(0.01, len - 0.3) / lengthShort);
      const amountLong = Math.floor(shortPieces / 2);
      const amountShort = Math.ceil(shortPieces % 2);

      const length = amountLong * lengthLong + amountShort * lengthShort - 0.3;
      amount += Math.ceil(Number((length / this.roofMultiplier()).toFixed(1))) + 1;
    });

    this.calcResults.roofBridgeConsoles = amount;

    const nPerBundle = 5;
    const bundles = Math.floor(amount / nPerBundle);
    amount = amount % nPerBundle;

    const artno = this.getRoofBridgeConsoleArtNo();
    const bundleArtno = artno + '-5';
    return [
      { art: RoofArticle.None, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} },
      { art: RoofArticle.None, artnr: bundleArtno, total: bundles, category: Category.Taksäkerhet, origins: {} },
    ] as ResultRow[];
  }

  public roofBridgeConsoleDistance(): number {
    return Math.round(this.roofMultiplier() * 1000);
  }

  public calculateRoofRail(): number {
    // Varje enhet är 2.1m lång, beräkna hur många enheter det tar för att täcka alla längder
    // BK-245: Vi kräver att resten från alla jämt beräknade takfotsräcken överstiger 50cm innan det beräknas en extra
    let amount = 0, skarvar = 0;
    for (const len of this.hatchSafetyLengths) {
      const rails = Math.ceil(Math.max(0.01, len - 0.5) / this.roofRailLength);
      amount += rails;
      skarvar += Math.max(rails - 1, 0);
    }
    this.calcResults.roofRail = amount;
    this.calcResults.roofRailSkarvar = skarvar;

    return amount;
  }

  public calculateRoofRailConsoles(): ResultRow[] {
    let amount = this.amountOfAccessories(this.hatchSafetyLengths, this.roofRailLength, 0, this.roofMultiplier());
    this.calcResults.roofRailConsoles = amount;

    const nPerBundle = 10;
    const bundles = Math.floor(amount / nPerBundle);
    amount = amount % nPerBundle;

    const artno = this.getRoofRailConsoleArtNo();
    const bundleArtno = artno + '-10';
    return [
      { art: RoofArticle.None, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} },
      { art: RoofArticle.None, artnr: bundleArtno, total: bundles, category: Category.Taksäkerhet, origins: {} },
    ] as ResultRow[];
  }

  public roofRailConsoleDistance(): number {
    return Math.round(this.roofMultiplier() * 1000);
  }

  public calculateFootPlats(): ResultRow[] {
    let amount = this.calcResults.snowSafetyConsoles + this.calcResults.roofBridgeConsoles + this.calcResults.roofRailConsoles;
    this.calcResults.footPlats = amount;

    const nPerBundle = 10;
    const bundles = Math.floor(amount / nPerBundle);
    amount = amount % nPerBundle;

    const artno = this.getFootPlatArtNo();
    const bundleArtno = artno + '-10';
    return [
      { art: RoofArticle.None, artnr: artno, total: amount, category: Category.Taksäkerhet, origins: {} },
      { art: RoofArticle.None, artnr: bundleArtno, total: bundles, category: Category.Taksäkerhet, origins: {} },
    ] as ResultRow[];
  }

  public calculateSnowSlideObstacles(): number {
    const takfotslängder = this.roof.calculateTakfotslängder();

    let sum = 0;
    const snowSlideObstacleLength = 1;
    for (const längd of takfotslängder) {
      sum += Math.ceil(längd / 1000 / snowSlideObstacleLength + 1);
    }
    return sum;
  }

  public calculateSnowSlideObstacleConsoles(): number {
    const takfotslängder = this.roof.calculateTakfotslängder();

    let sum = 0;
    const snowSlideObstacleLength = 0.6;
    for (const längd of takfotslängder) {
      sum += Math.ceil(längd / 1000 / snowSlideObstacleLength + 1);
    }
    this.calcResults.snowSlideConsoles = sum;
    return sum;
  }

  public addArticleRowByArtnr(sum: ResultRow[], artnr: string, amount: number, category: Category) {
    if (amount <= 0) return;
    if ((artnr?.length ?? 0) <= 0) return;

    addResult(sum, { art: RoofArticle.None, artnr, total: amount, category: category, origins: {} });
  }

  public async calculate(): Promise<ResultRow[]> {
    if (!this.roof.mainPart) throw Error('Main roofpart not set');
    if (!this.selColor) throw Error('Color not set');
    if (this.useSAFE && (this.snowSafetyLengths?.length || this.snowSafetyRaisedLengths?.length) && !this.snowZone)
      throw Error('Snow-zone not set');
    if (this.roof.market == MarketStr.Tyskland) return []; // BENT-214: KRI doesn't support Safe yet

    let results: ResultRow[] = [];

    // Add roof-safety-system independent articles
    this.addArticleRowByArtnr(results, this.battenStepArtNo, this.selBattenSteps, Category.Taksäkerhet);
    this.addArticleRowByArtnr(results, this.mountingRailArtNo, this.selMountingRail, Category.Taksäkerhet);
    this.addArticleRowByArtnr(results, this.slipProtectionArtNo, this.selSlipProtection, Category.Taksäkerhet);
    this.addArticleRowByArtnr(results, this.roofHatchArtNo, this.selRoofHatch, Category.Taksäkerhet);
    this.addArticleRowByArtnr(results, this.safetyHookArtNo, this.selSafetyHook, Category.Taksäkerhet);
    this.addArticleRowByArtnr(results, this.snowHookArtNo, this.selSnowHook, Category.Taksäkerhet);
    if (this.selSnowSlideProtection) {
      this.addArticleRowByArtnr(results, this.snowSlideObstacleArtNo, this.calculateSnowSlideObstacles(), Category.Taksäkerhet);
      this.addArticleRowByArtnr(results, this.snowSlideObstacleKonsolArtNo, this.calculateSnowSlideObstacleConsoles(), Category.Taksäkerhet);
    }

    // BENT-332: 0830xx shouldn't be calculated in SAN
    if (this.roof.market !== MarketStr.Norge || ![RoofTile.Carisma, RoofTile.Piano].includes(this.roof.selection.tileFamily)) {
      this.addArticleRowByArtnr(results, this.iceStopperArtNo, this.selIceStopper, Category.Taksäkerhet);
    }

    results = addResults(results, await this.calculateTrackTiles());

    if (this.useSAFE) {
      this.addArticleRowByArtnr(results, this.getSnowSafetyArtNo(), this.calculateSnowSafety(), Category.Taksäkerhet);
      results = addResults(results, this.calculateSnowSafetyConsoles());
      results = addResults(results, this.calculateElevationMounts());
      results = addResults(results, this.calculateRoofBridge());
      results = addResults(results, this.calculateRoofBridgeConsoles());
      this.addArticleRowByArtnr(results, this.getRoofRailArtNo(), this.calculateRoofRail(), Category.Taksäkerhet);
      results = addResults(results, this.calculateRoofRailConsoles());
      this.addArticleRowByArtnr(results, this.getRoofHatchRailArtNo(), this.roofRailsAmount, Category.Taksäkerhet);
      results = addResults(results, this.calculateFootPlats());
      this.addArticleRowByArtnr(results, this.fasteningEyeArtNo, this.fasteningEyeAmount, Category.Taksäkerhet);
      results = addResults(results, await this.calculateSafeTrackTiles());
    }

    // Remove empty result rows
    removeZeroQtyRows(results);

    // Multiply by number of roofs
    for (const row of results) row.total *= this.roof.amount;

    return results;
  }
}
